import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { EChartsOption } from 'echarts';
import type { ECharts } from 'echarts';
import { RecordData } from 'src/app/types';
import { GOST_I18N } from '../../../cards/recordings/gost.i18n';
import { RecordingEvent, SeekEvent } from '../../measured-data-details.types';
import { MeasurementEvent, ParsedMeasurement } from '../../models/parsed-measurement.model';
import { NeckOrientation } from '../../services/neck-angle.service';

import { MeasuredDataNeckAngleCardComponent } from '../neck-angle-card/measured-data-neck-angle-card.component';
import { DataFooterComponent } from '../shared/data-footer/data-footer.component';
import { DetailsHeaderComponent } from '../shared/details-header/details-header.component';
import { DisclaimerComponent } from '../shared/disclaimer/disclaimer.component';
import { EventTableComponent } from '../shared/event-table/event-table.component';
import { MeasuredDataWaveCardComponent } from '../wave-card/measured-data-wave-card.component';

/** 試行の1行表示用 */
export interface SstpTrialRow {
  label: string;
  swallowPresent: boolean;
  swallowLabel: string;
  coughPresent: boolean;
  coughLabel: string;
  abnormalSoundPresent: boolean;
  abnormalSoundLabel: string;
  /** 詳細行（嚥下遅延、複数嚥下、咽頭残留、その他）*/
  details: SstpTrialDetail[];
  measured: boolean;
}

export interface SstpTrialDetail {
  label: string;
  present: boolean;
  displayLabel: string;
}

@Component({
  selector: 'app-protocol-sstp',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    DetailsHeaderComponent,
    EventTableComponent,
    DataFooterComponent,
    DisclaimerComponent,
    MeasuredDataWaveCardComponent,
    MeasuredDataNeckAngleCardComponent,
  ],
  templateUrl: './protocol-sstp.component.html',
  styleUrl: './protocol-sstp.component.scss',
})
export class ProtocolSstpComponent implements OnDestroy {
  // ── Data inputs ──
  readonly recordData = input.required<RecordData>();
  readonly parsedMeasurement = input<ParsedMeasurement | null>(null);

  // ── Wave card inputs ──
  readonly recordtype = input<number>(0);
  readonly recordLoadingSuccess = input<boolean>(true);
  readonly waveLoadingPercent = input<number | null>(null);
  readonly waveProcessing = input<boolean>(false);
  readonly waveDownloading = input<boolean>(false);
  readonly waveDownloadPercent = input<number | null>(null);
  readonly waveErrorMessage = input<string | null>(null);
  readonly selectedEvent = input<RecordingEvent | null>(null);
  readonly currentStartOffset = input<number>(0);
  readonly playing = input<boolean>(false);
  readonly timelineOffsetReference = input<number>(0);
  readonly zoomValue = input<number>(0);
  readonly showWaveCard = input<boolean>(true);

  // ── Neck angle inputs ──
  readonly chartOption = input<EChartsOption>({});
  readonly currentChartOption = input<EChartsOption>({});
  readonly currentOrientation = input<NeckOrientation | null>(null);

  // ── Wave card outputs ──
  @Output() readonly offsetToggle = new EventEmitter<boolean>();
  @Output() readonly seek = new EventEmitter<number>();
  @Output() readonly eventSeek = new EventEmitter<SeekEvent>();
  @Output() readonly playPauseClicked = new EventEmitter<void>();
  @Output() readonly zoomMinusClicked = new EventEmitter<void>();
  @Output() readonly zoomResetClicked = new EventEmitter<void>();
  @Output() readonly zoomPlusClicked = new EventEmitter<void>();

  // ── Neck angle outputs ──
  @Output() readonly chartInit = new EventEmitter<ECharts>();
  @Output() readonly currentChartInit = new EventEmitter<ECharts>();

  @Output() readonly waveHostReady = new EventEmitter<MeasuredDataWaveCardComponent>();
  private destroyed = false;
  private latestWaveCardRef?: MeasuredDataWaveCardComponent;

  @ViewChild(MeasuredDataWaveCardComponent)
  set waveCardRef(comp: MeasuredDataWaveCardComponent | undefined) {
    if (!comp || this.destroyed) return;
    this.latestWaveCardRef = comp;
    queueMicrotask(() => {
      if (this.destroyed || this.latestWaveCardRef !== comp) return;
      this.waveHostReady.emit(comp);
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.latestWaveCardRef = undefined;
  }

  // ── Local state ──
  protected readonly trialsExpanded = signal(true);
  protected readonly detailExpandedIndex = signal<number | null>(null);

  protected toggleTrials(): void {
    this.trialsExpanded.update(v => !v);
  }

  protected toggleDetail(index: number): void {
    this.detailExpandedIndex.update(v => v === index ? null : index);
  }

  // ── Computed: header ──
  protected readonly patientName = computed(() =>
    this.recordData()?.data?.userName ?? '',
  );

  protected readonly measureDate = computed(() => {
    const d = this.recordData()?.data;
    const createdAt = d?.createdAt ?? d?.recordedAt ?? '';
    const offsetMatch = createdAt.match(/([+-])(\d{2}):(\d{2})$/);
    const hasNonZeroOffset =
      offsetMatch &&
      !(offsetMatch[1] === '+' && offsetMatch[2] === '00' && offsetMatch[3] === '00');

    if (hasNonZeroOffset && d?.createdAtLocationStr) {
      const dateStr = d.createdAtLocationStr.replace(/[+-]\d{2}:\d{2}$/, '').trim();
      if (!dateStr) return '';
      const h = parseInt(offsetMatch[2], 10);
      const m = parseInt(offsetMatch[3], 10);
      const tzLabel = m > 0
        ? `UTC${offsetMatch[1]}${h}:${String(m).padStart(2, '0')}`
        : `UTC${offsetMatch[1]}${h}`;
      return `${dateStr} (${tzLabel})`;
    }

    const raw = d?.createdAtLocalStr ?? d?.recordedAt ?? createdAt;
    const dateStr = raw.replace(/[+-]\d{2}:\d{2}$/, '').trim();
    if (!dateStr) return '';
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz ? `${dateStr} (${tz})` : dateStr;
    } catch {
      return dateStr;
    }
  });

  protected readonly measurer = computed(() => {
    const rd = this.recordData();
    const d = rd?.data;
    if (d?.recorderName) return $localize`:@@details.protocolSstp.measurer:計測者: ${d.recorderName}`;
    const m = rd?.meta?.measurer;
    if (m?.lastName || m?.firstName) return $localize`:@@details.protocolSstp.measurer:計測者: ${(m.lastName ?? '') + (m.firstName ?? '')}`;
    return '';
  });

  // ── Computed: KPI ──
  protected readonly sstpPath = computed(() => {
    const p = this.recordData()?.data?.sstpPath as { path5ml?: string; path30ml?: string } | undefined;
    return {
      path5ml: (p?.path5ml ?? '').trim().toUpperCase(),
      path30ml: (p?.path30ml ?? '').trim().toUpperCase(),
    };
  });

  protected readonly isStable = computed(() => {
    const p = this.sstpPath();
    return p.path5ml === 'C' && (!p.path30ml || p.path30ml === 'C');
  });

  protected readonly hasWarning = computed(() => !this.isStable());

  protected readonly resultLabel = computed(() =>
    this.isStable()
      ? $localize`:@@details.protocolSstp.resultLabelStable:安定した飲水`
      : $localize`:@@details.protocolSstp.resultLabelWarning:問題を検出`,
  );

  protected readonly pathDescription5ml = computed(() =>
    this.pathLabel(this.sstpPath().path5ml),
  );

  protected readonly pathDescription30ml = computed(() =>
    this.pathLabel(this.sstpPath().path30ml),
  );

  private pathLabel(code: string): string {
    if (!code) return $localize`:@@details.protocolSstp.pathLabelUnmeasured:未測定`;
    switch (code) {
      case 'C': return $localize`:@@details.protocolSstp.pathLabelOk:問題なく飲み込めた`;
      case 'I': return $localize`:@@details.protocolSstp.pathLabelIssue:飲み込みに課題あり`;
      case 'F': return $localize`:@@details.protocolSstp.pathLabelFail:嚥下なし`;
      default: return code;
    }
  }

  // ── Computed: Trials ──
  protected readonly trialRows = computed<SstpTrialRow[]>(() => {
    const data = this.recordData()?.data;
    const stepwiseWST: any[] = data?.stepwiseWST ?? [];
    const challenge = data?.challenge as any | undefined;
    const rows: SstpTrialRow[] = [];
    const sc = GOST_I18N.sc;

    // stepwiseWST → 各ステップの各ラウンド
    for (const step of stepwiseWST) {
      const amount = step.amount ?? 0;
      const withThickness = step.withThickness ?? false;

      // 1回目
      if (step.firstTrySwallowResult || step.firstTryCoughResult) {
        const firstLabel = withThickness
          ? $localize`:@@details.protocolSstp.trialFirstWithThickness:${amount}:amount:ml 1回目（とろみ）`
          : $localize`:@@details.protocolSstp.trialFirst:${amount}:amount:ml 1回目`;
        rows.push(this.buildTrialRow(
          firstLabel,
          step.firstTrySwallowResult,
          step.firstTryCoughResult,
          step.firstTryAbnormalResult,
          {
            swallowDelay: undefined,
            multiSwallows: undefined,
            throatClear: undefined,
            otherAbbration: undefined,
          },
          sc,
        ));
      }

      // 2回目
      if (step.secondTrySwallowResult || step.secondTryCoughResult) {
        const secondLabel = withThickness
          ? $localize`:@@details.protocolSstp.trialSecondWithThickness:${amount}:amount:ml 2回目（とろみ）`
          : $localize`:@@details.protocolSstp.trialSecond:${amount}:amount:ml 2回目`;
        rows.push(this.buildTrialRow(
          secondLabel,
          step.secondTrySwallowResult,
          step.secondTryCoughResult,
          step.secondTryAbnormalResult,
          {
            swallowDelay: undefined,
            multiSwallows: undefined,
            throatClear: undefined,
            otherAbbration: undefined,
          },
          sc,
        ));
      }
    }

    // challenge (30ml)
    if (challenge?.swallowCount != null || challenge?.coughResult) {
      rows.push(this.buildTrialRow(
        $localize`:@@details.protocolSstp.trialChallenge:30ml チャレンジ`,
        challenge.fully,
        challenge.coughResult,
        challenge.abnormalSound,
        {
          swallowDelay: undefined,
          multiSwallows: undefined,
          throatClear: undefined,
          otherAbbration: undefined,
        },
        sc,
      ));
    }

    return rows;
  });

  // ── Computed: events, footer ──
  protected readonly events = computed<MeasurementEvent[]>(() =>
    this.parsedMeasurement()?.events ?? [],
  );

  protected readonly dataId = computed(() => this.recordData()?.id ?? '');

  protected readonly downloadUrl = computed(() =>
    this.recordData()?.data?.storage?.csvUrl ?? '',
  );

  protected readonly disclaimer = $localize`:@@details.protocolSstp.disclaimer:SSTPはスクリーニング検査です。判定には観察所見とAI推定値を含むため、最終的な臨床判断は専門医の評価と併せて行ってください。`;

  protected onEventSeek(event: SeekEvent): void {
    this.eventSeek.emit(event);
  }

  // ── Private ──
  private buildTrialRow(
    label: string,
    swallowResult: string | undefined,
    coughResult: string | undefined,
    abnormalSoundResult: string | undefined,
    extras: {
      swallowDelay?: string;
      multiSwallows?: string;
      throatClear?: string;
      otherAbbration?: string;
    },
    sc: typeof GOST_I18N.sc,
  ): SstpTrialRow {
    const swallow = this.resolvePresence(swallowResult);
    const cough = this.resolvePresence(coughResult);
    const abnormalSound = this.resolvePresence(abnormalSoundResult);

    const details: SstpTrialDetail[] = [];
    if (extras.swallowDelay !== undefined) {
      const r = this.resolvePresence(extras.swallowDelay);
      details.push({ label: sc.swallowDelay.label, present: r.present, displayLabel: r.label });
    }
    if (extras.multiSwallows !== undefined) {
      const r = this.resolvePresence(extras.multiSwallows);
      details.push({ label: sc.multiSwallows.label, present: r.present, displayLabel: r.label });
    }
    if (extras.throatClear !== undefined) {
      const r = this.resolvePresence(extras.throatClear);
      details.push({ label: sc.throatClear.label, present: r.present, displayLabel: r.label });
    }
    if (extras.otherAbbration !== undefined) {
      const r = this.resolvePresence(extras.otherAbbration);
      details.push({ label: sc.otherAbbration.label, present: r.present, displayLabel: r.label });
    }

    const measured = !!(swallowResult || coughResult || abnormalSoundResult);

    return {
      label,
      swallowPresent: swallow.present,
      swallowLabel: swallow.label,
      coughPresent: cough.present,
      coughLabel: cough.label,
      abnormalSoundPresent: abnormalSound.present,
      abnormalSoundLabel: abnormalSound.label,
      details,
      measured,
    };
  }

  private resolvePresence(value: unknown): { label: string; present: boolean } {
    const str = String(value ?? '').trim().toLowerCase();
    if (!str) return { label: '—', present: false };
    if (['true', 'tp', 'fn', '1'].includes(str) || str.includes('あり') || str.includes('切迫')) {
      return { label: $localize`:@@details.protocolSstp.presenceYes:あり`, present: true };
    }
    if (['false', 'tn', 'fp', '0'].includes(str) || str.includes('なし') || str.includes('良好')) {
      return { label: $localize`:@@details.protocolSstp.presenceNo:なし`, present: false };
    }
    return { label: String(value).trim() || '—', present: false };
  }

}
